import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, Lock, Plus, RotateCcw, Trash2, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { REPORT_DEFS } from './reportSchemas';
import { useReportDraft } from './useReportDraft';

/**
 * Generic editor for the row-based DOLE forms (everything except SPRS). Cell layout and
 * types come from the form's schema; all persistence is delegated to useReportDraft.
 */
export default function ReportEditor({ reportType, period, authHeaders, onBack, settings, officeName }) {
  const { t } = useTranslation();
  const def = REPORT_DEFS[reportType];
  const draft = useReportDraft(reportType, period, authHeaders);
  const { data, deltas, meta, loading, loadError, saving, busy, busyRef, load, commit, applyAndReload, reset, download } = draft;

  const [rows, setRows] = useState([]);
  useEffect(() => { setRows(data?.rows || []); }, [data]);

  const editCell = (rowKey, field, value) => {
    if (busyRef.current || !deltas) return;
    setRows((prev) => prev.map((r) => (r._key === rowKey ? { ...r, [field]: value } : r)));
    const isManual = rowKey.startsWith('m_');
    const next = isManual
      ? { ...deltas, extra_rows: deltas.extra_rows.map((r) => (r._key === rowKey ? { ...r, [field]: value } : r)) }
      : { ...deltas, overrides: { ...deltas.overrides, [rowKey]: { ...(deltas.overrides[rowKey] || {}), [field]: value } } };
    commit(next);
  };

  const revertCell = (rowKey, field) => applyAndReload((current) => {
    const { [field]: _removed, ...rest } = current.overrides[rowKey] || {};
    const nextOverrides = { ...current.overrides };
    if (Object.keys(rest).length) nextOverrides[rowKey] = rest; else delete nextOverrides[rowKey];
    return { ...current, overrides: nextOverrides };
  });

  const deleteRow = (rowKey) => {
    if (busyRef.current || !deltas) return;
    setRows((prev) => prev.filter((r) => r._key !== rowKey));
    const next = rowKey.startsWith('m_')
      ? { ...deltas, extra_rows: deltas.extra_rows.filter((r) => r._key !== rowKey) }
      : { ...deltas, deleted_keys: [...deltas.deleted_keys, rowKey] };
    commit(next);
  };

  const addRow = () => {
    if (busyRef.current || !deltas) return;
    const blank = Object.fromEntries(def.columns.map((c) => [
      c.field, c.type === 'check' ? false : c.type === 'select' ? c.options[0] : '',
    ]));
    const key = `m_${(window.crypto?.randomUUID?.() || String(Date.now() + Math.random()))}`;
    const row = { ...blank, _key: key, _source: 'manual' };
    setRows((prev) => [...prev, row]);
    commit({ ...deltas, extra_rows: [...deltas.extra_rows, row] });
  };

  const resetAll = () => {
    if (busyRef.current) return;
    if (!window.confirm(t('peso.reportResetConfirm', 'Discard all edits and reload platform data?'))) return;
    reset();
  };

  const isOverridden = (row, field) => deltas?.overrides?.[row._key]?.[field] !== undefined;

  const withProvenance = (row, col, control) => {
    const over = isOverridden(row, col.field);
    return (
      <div className={`relative ${over ? 'ring-1 ring-amber-400 bg-amber-50 rounded' : ''}`}>
        {control}
        {over && row._source === 'platform' && (
          <button type="button" title={t('peso.revertCell', 'Revert to platform data')}
            onClick={() => revertCell(row._key, col.field)}
            className="absolute -top-1 -right-1 text-amber-600 hover:text-amber-800">
            <X size={12} />
          </button>
        )}
      </div>
    );
  };

  const renderCell = (row, col) => {
    const val = row[col.field];
    if (col.type === 'check') {
      return withProvenance(row, col, (
        <button type="button" onClick={() => editCell(row._key, col.field, !val)} aria-label={col.label}
          className="w-8 h-8 text-emerald-700 font-bold hover:bg-emerald-50 rounded">{val ? '✓' : ''}</button>
      ));
    }
    if (col.type === 'mf') {
      return withProvenance(row, col, (
        <div className="flex gap-1">
          {['M', 'F'].map((mf) => (
            <button key={mf} type="button" onClick={() => editCell(row._key, col.field, mf)}
              className={`px-2 py-1 text-xs rounded border ${val === mf ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-300'}`}>{mf}</button>
          ))}
        </div>
      ));
    }
    if (col.type === 'youth') {
      // Auto-derived from date of birth; editable only for the rare row with no DOB, so
      // staff can never accidentally overwrite a correct age classification.
      const locked = !row.dob_missing;
      return withProvenance(row, col, (
        <div className="flex items-center gap-1">
          {[['youth', 'Youth'], ['nonyouth', 'Non-Youth']].map(([v, label]) => (
            <button key={v} type="button" disabled={locked}
              onClick={() => !locked && editCell(row._key, col.field, v)}
              className={`px-2 py-1 text-[11px] rounded border ${val === v ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-300'} ${locked ? 'opacity-70 cursor-default' : ''}`}>
              {label}
            </button>
          ))}
          {locked
            ? <Lock size={11} className="text-slate-300" title={t('peso.youthAuto', 'Auto from date of birth')} />
            : <span className="text-[10px] text-amber-600">{t('peso.noDob', 'no DOB')}</span>}
        </div>
      ));
    }
    if (col.type === 'select') {
      return withProvenance(row, col, (
        <select value={val || ''} onChange={(e) => editCell(row._key, col.field, e.target.value)}
          className="border rounded px-1 py-1 text-sm bg-white">
          {!val && <option value="" />}
          {col.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ));
    }
    return withProvenance(row, col, (
      <Input value={val ?? ''} type={col.type === 'number' ? 'number' : 'text'}
        onChange={(e) => editCell(row._key, col.field, col.type === 'number' ? Number(e.target.value) : e.target.value)}
        className="h-8 text-sm border-transparent bg-transparent" />
    ));
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">{t('peso.loading', 'Loading…')}</div>;
  }
  if (loadError || !deltas) {
    return (
      <Card>
        <CardContent className="p-6 text-sm space-y-3">
          <p>{t('peso.reportLoadFailed', 'Failed to load report')}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load}>{t('peso.retry', 'Retry')}</Button>
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft size={14} className="mr-1" /> {t('peso.back', 'Back')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const monthLabel = new Date(`${period}-01T00:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const sigs = settings?.signatories || {};

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
            <ArrowLeft size={14} className="mr-1" /> {t('peso.backToReports', 'All reports')}
          </Button>
          <CardTitle className="text-sm mt-1">{def.title} — {monthLabel}</CardTitle>
          {meta?.updated_by_name && (
            <p className="text-xs text-muted-foreground">{t('peso.lastEditedBy', 'Last edited by')} {meta.updated_by_name}</p>
          )}
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <span className="text-xs text-muted-foreground min-w-[52px]">
            {saving ? t('peso.saving', 'Saving…') : meta?.draft_exists ? t('peso.saved', 'Saved') : ''}
          </span>
          <Button variant="outline" size="sm" onClick={resetAll} disabled={busy}>
            <RotateCcw size={14} className="mr-1" /> {t('peso.reportReset', 'Reset')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => download('docx')}><Download size={14} className="mr-1" /> Word</Button>
          <Button variant="outline" size="sm" onClick={() => download('xlsx')}><Download size={14} className="mr-1" /> Excel</Button>
        </div>
      </CardHeader>

      <CardContent>
        <div className="text-center text-xs leading-5 mb-3">
          <div>Republic of the Philippines</div>
          <div className="font-semibold">DEPARTMENT OF LABOR AND EMPLOYMENT</div>
          <div>Regional Office No. {settings?.dole_region || '7'}</div>
          <div>{settings?.field_office || 'BOHOL FIELD OFFICE'}</div>
          {def.footer === 'lmi' && settings?.lmi_certificate_number && (
            <div>Certificate Number: {settings.lmi_certificate_number}</div>
          )}
          {def.docTitle.map((line) => <div key={line} className="font-bold">{line}</div>)}
          <div>For the month of {monthLabel.toUpperCase()}</div>
          <div>PESO: {(officeName || '').toUpperCase()}</div>
        </div>

        <div className={`overflow-x-auto ${busy ? 'opacity-60 pointer-events-none' : ''}`}>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-slate-50">
                {def.columns.map((c) => (
                  <th key={c.field} className={`border px-2 py-1 text-left text-xs font-bold ${c.width || ''}`}>{c.label}</th>
                ))}
                {!def.fixedRows && <th className="border px-2 py-1 w-8" />}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={def.columns.length + 1} className="border px-3 py-6 text-center text-muted-foreground">
                    {t('peso.reportNoRows', 'No platform data for this month yet — add rows manually if needed.')}
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row._key} className={row._source === 'manual' ? 'bg-blue-50/40' : ''}>
                  {def.columns.map((c) => (
                    <td key={c.field} className="border px-1 py-0.5">{renderCell(row, c)}</td>
                  ))}
                  {!def.fixedRows && (
                    <td className="border px-1 text-center">
                      <button type="button" onClick={() => deleteRow(row._key)} title={t('peso.deleteRow', 'Delete row')}
                        className="text-red-400 hover:text-red-600"><Trash2 size={13} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center gap-3 mt-3 flex-wrap">
          {def.addRowEnabled && !def.fixedRows && (
            <Button variant="outline" size="sm" onClick={addRow} disabled={busy}>
              <Plus size={14} className="mr-1" /> {t('peso.addRow', 'Add row')}
            </Button>
          )}
          <span className="text-xs text-muted-foreground">
            {t('peso.reportRowCount', { count: rows.length, defaultValue: `${rows.length} rows` })}
            {' · '}
            <span className="inline-block w-2 h-2 rounded-sm bg-blue-200 align-middle" />{' '}
            {t('peso.reportManualLegend', 'manually added')}
          </span>
        </div>

        <div className="mt-5 text-xs">
          {def.footer === 'lmi' ? (
            <div className="flex gap-12 flex-wrap">
              <div>Reported by:<br /><span className="font-bold">{sigs.reported_by?.name}</span><br />{sigs.reported_by?.title}</div>
              <div>Certified by:<br /><span className="font-bold">{sigs.certified_by?.name}</span><br />{sigs.certified_by?.title}</div>
            </div>
          ) : (
            <div>CERTIFIED CORRECT:<br /><span className="font-bold">{sigs.certified_by?.name}</span><br />{sigs.certified_by?.title}</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
